import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Cross, Lock, Chrome } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";

export default function AdminLogin() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (location.state?.error) setError(location.state.error);
  }, [location.state]);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  if (user) return null;

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(apiError(err, "Login failed. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-navy grain-overlay px-4" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-3" data-testid="login-logo">
            <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-gold text-navy">
              <Cross className="h-6 w-6" strokeWidth={2.5} />
            </span>
          </Link>
          <h1 className="mt-5 font-heading text-2xl font-bold text-cream">Church Administration</h1>
          <p className="mt-1 text-sm text-cream/60">Christ Church CNI, Rajpura — authorised access only</p>
        </div>
        <form onSubmit={submit} className="rounded-lg border border-cream/10 bg-navy-light/60 p-8 backdrop-blur" data-testid="admin-login-form">
          {error && (
            <div className="mb-5 rounded-md border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200" role="alert" data-testid="login-error">
              {error}
            </div>
          )}
          <div className="space-y-5">
            <div>
              <Label htmlFor="admin-email" className="text-cream/80">Email</Label>
              <Input
                id="admin-email"
                type="email"
                required
                autoComplete="email"
                data-testid="admin-email-input"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 border-cream/20 bg-navy text-cream placeholder:text-cream/30"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <Label htmlFor="admin-password" className="text-cream/80">Password</Label>
              <Input
                id="admin-password"
                type="password"
                required
                autoComplete="current-password"
                data-testid="admin-password-input"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 border-cream/20 bg-navy text-cream placeholder:text-cream/30"
                placeholder="••••••••"
              />
            </div>
            <div className="text-right">
              <Link to="/admin/forgot-password" className="text-xs text-gold hover:text-gold-dark transition-colors" data-testid="forgot-password-link">
                Forgot Password?
              </Link>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gold text-navy hover:bg-gold-dark hover:text-cream transition-colors"
              data-testid="admin-login-submit"
            >
              <Lock className="mr-2 h-4 w-4" /> {loading ? "Signing in…" : "Sign In"}
            </Button>
          </div>
        </form>
        <div className="mt-5 flex items-center gap-3" aria-hidden="true">
          <div className="h-px flex-1 bg-cream/15" />
          <span className="text-xs text-cream/50">or</span>
          <div className="h-px flex-1 bg-cream/15" />
        </div>
        <Button
          type="button"
          onClick={googleSignIn}
          variant="outline"
          className="mt-5 w-full border-cream/25 bg-transparent text-cream hover:bg-cream/10 hover:text-cream"
          data-testid="google-signin-btn"
        >
          <Chrome className="mr-2 h-4 w-4" /> Sign in with Google
        </Button>
        <p className="mt-6 text-center text-xs text-cream/40">
          <Link to="/" className="hover:text-gold transition-colors" data-testid="back-to-site-link">← Back to church website</Link>
        </p>
      </div>
    </div>
  );
}
